import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { ACCESS_ERROR_KEY, api, errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => { const m = sessionStorage.getItem(ACCESS_ERROR_KEY); if (m) sessionStorage.removeItem(ACCESS_ERROR_KEY); return m || ""; });
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const resend = async (e) => {
    e.preventDefault(); setResendMsg("");
    try { await api.post("/access/resend", { email: resendEmail }); setResendMsg("E-mail de acesso reenviado! Verifique sua caixa de entrada e o spam."); }
    catch (err) { setResendMsg(errMsg(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(f.email, f.password);
      toast.success(`Bem-vindo de volta, ${u.name.split(" ")[0]}!`);
      nav(u.onboarding_done ? "/" : "/onboarding");
    } catch (err) { setError(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Bem-vindo de volta" subtitle="Entre para ver a saúde financeira do seu negócio."
      footer={<div className="space-y-2">
        <p>Comprou e não recebeu o e-mail? <button type="button" onClick={() => setResendOpen(!resendOpen)} data-testid="link-resend" className="text-primary font-medium hover:underline">Reenviar acesso</button></p>
        {resendOpen && (
          <form onSubmit={resend} className="flex gap-2" data-testid="resend-form">
            <Input data-testid="resend-email" type="email" required placeholder="e-mail usado na compra" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} />
            <Button type="submit" variant="outline" data-testid="resend-submit">Enviar</Button>
          </form>
        )}
        {resendMsg && <p data-testid="resend-msg" className="text-xs">{resendMsg}</p>}
        <p>Tem um código de ativação? <Link to="/cadastro" data-testid="link-register" className="text-primary font-medium hover:underline">Ativar com código</Link></p>
      </div>}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="E-mail"><Input data-testid="login-email" type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="voce@seunegocio.com" className="h-11 rounded-xl" /></Field>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between"><label className="text-sm font-medium">Senha</label><Link to="/esqueci-senha" data-testid="link-forgot" className="text-xs text-muted-foreground hover:text-primary transition-colors">Esqueci minha senha</Link></div>
          <PasswordInput data-testid="login-password" required autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="••••••••" className="h-11 rounded-xl" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2">{error}</p>}
        <Button data-testid="login-submit" type="submit" className="w-full h-11 rounded-xl font-semibold" disabled={loading}>{loading ? "Entrando..." : "Entrar"}</Button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="flex-1 border-t border-border" />ou<span className="flex-1 border-t border-border" /></div>
        <Button type="button" variant="outline" onClick={google} data-testid="google-login-btn" className="w-full h-11 rounded-xl font-medium">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" aria-hidden><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Entrar com Google
        </Button>
      </form>
    </AuthShell>
  );
}
